import { useTranslation } from "react-i18next";
import { cn } from "@v2/lib/utils";
import { statusLabel } from "../../lib/labels";
import { groupByRole } from "../../lib/roster";
import type { Player, ResponseStatus, SessionCallup } from "../../types";

export type OverviewRow = { player: Player; status: ResponseStatus; callup?: SessionCallup };

const STATUS_MARK: Record<ResponseStatus, { label: string; tone: string }> = {
  present: { label: "P", tone: "bg-[hsl(var(--ok))] text-background" },
  late: { label: "R", tone: "bg-[hsl(var(--warn))] text-background" },
  absent: { label: "A", tone: "bg-[hsl(var(--danger))] text-background" },
  no_response: { label: "?", tone: "bg-[hsl(var(--faint))] text-background" },
};

// Prima chi puo' esserci, poi chi non ha risposto, in fondo gli assenti.
const STATUS_ORDER: ResponseStatus[] = ["present", "late", "no_response", "absent"];
const byAvailability = (a: OverviewRow, b: OverviewRow) =>
  STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status) ||
  (a.player.last_name || a.player.display_name).localeCompare(b.player.last_name || b.player.display_name);

const isCalled = (row: OverviewRow) => row.callup?.status === "confirmed";

function shortName({ first_name, last_name, display_name }: Player) {
  if (!first_name || !last_name || display_name !== `${first_name} ${last_name}`) return display_name;
  return `${first_name[0]}. ${last_name}`;
}

export function CallupTiles({
  rows,
  twoColumns,
  onToggle,
}: {
  rows: OverviewRow[];
  twoColumns?: boolean;
  onToggle: (playerId: string, shouldCallup: boolean) => void;
}) {
  return (
    <div className={cn("grid grid-cols-2 gap-1", !twoColumns && "sm:grid-cols-3 lg:grid-cols-4")}>
      {[...rows].sort(byAvailability).map((row) => {
        const called = isCalled(row);
        const mark = STATUS_MARK[row.status];
        return (
          <button
            key={row.player.id}
            type="button"
            aria-pressed={called}
            aria-label={`${row.player.display_name}, ${statusLabel(row.status)}`}
            title={row.player.display_name}
            onClick={() => onToggle(row.player.id, !called)}
            className={cn(
              "flex min-h-11 min-w-0 items-center gap-1.5 rounded-xl px-2 text-left text-sm transition-colors",
              called
                ? row.callup?.notified_at
                  ? "bg-accent text-accent-foreground"
                  : "bg-accent/25 ring-1 ring-inset ring-accent"
                : row.status === "absent"
                  ? "text-muted-foreground ring-1 ring-inset ring-border"
                  : "bg-raised",
            )}
          >
            <span className={cn("inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-xs font-bold", mark.tone)}>
              {mark.label}
            </span>
            <span className="min-w-0 flex-1 truncate font-medium">{shortName(row.player)}</span>
            <span className={cn("shrink-0 text-xs", !called && "text-[hsl(var(--faint))]")}>{row.player.role_code}</span>
          </button>
        );
      })}
    </div>
  );
}

export function CallupOverview({
  rows,
  allRows,
  twoColumns,
  onToggle,
}: {
  // rows rispetta il filtro attivo; allRows serve ai conteggi di reparto, che
  // non devono cambiare quando la lista mostra solo una risposta.
  rows: OverviewRow[];
  allRows: OverviewRow[];
  twoColumns?: boolean;
  onToggle: (playerId: string, shouldCallup: boolean) => void;
}) {
  const { t } = useTranslation("sessions");
  const { t: tPlayers } = useTranslation("players");
  const visible = new Set(rows.map((row) => row.player.id));
  const groups = groupByRole(allRows, (row) => row.player.role_code);

  return (
    <div className="space-y-3">
      {groups.map((group) => {
        const shown = group.rows.filter((row) => visible.has(row.player.id));
        if (shown.length === 0) return null;
        return (
          <section key={group.key}>
            <h4 className="flex flex-wrap items-baseline justify-between gap-x-3 px-1 pb-1.5 font-semibold">
              {tPlayers(`grid.groups.${group.key}`)}
              <span className="text-[13px] font-normal text-muted-foreground tabular-nums">
                {t("board.groupSummary", {
                  called: group.rows.filter(isCalled).length,
                  available: group.rows.filter((row) => row.status === "present" || row.status === "late").length,
                })}
              </span>
            </h4>
            <CallupTiles rows={shown} twoColumns={twoColumns} onToggle={onToggle} />
          </section>
        );
      })}
    </div>
  );
}
